import { useState, useEffect, useCallback } from 'react';
import { X, Loader2 } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { usePatchRecurrence } from '../../hooks/useRecurrences.js';
import CategoryCombobox from '../CategoryCombobox.jsx';
import { formatCurrency } from '../../lib/format.js';

const FREQ_LABELS = {
  weekly: 'hebdomadaire',
  monthly: 'mensuelle',
  quarterly: 'trimestrielle',
  yearly: 'annuelle',
  annual: 'annuelle',
  custom: 'personnalisée',
};

const VAT_OPTIONS = [
  { label: '0 %', value: 0 },
  { label: '5,5 %', value: 5.5 },
  { label: '10 %', value: 10 },
  { label: '20 %', value: 20 },
];

/**
 * Slide-over drawer pour valider les récurrences détectées automatiquement.
 * Bottom sheet sur mobile, drawer droite sur desktop.
 *
 * Props :
 *   isOpen             — boolean
 *   onClose            — () => void
 *   pendingRecurrences — array depuis forecasts/tree response
 */
export default function PendingRecurrencesPanel({ isOpen, onClose, pendingRecurrences = [] }) {
  const qc = useQueryClient();
  const patchRecurrence = usePatchRecurrence();

  // État local de la liste (pour optimistic remove)
  const [localList, setLocalList] = useState([]);
  // États par item : { [id]: { categoryId, vatRate } }
  const [itemStates, setItemStates] = useState({});
  const [allDone, setAllDone] = useState(false);

  // Sync local list quand la prop change
  useEffect(() => {
    if (isOpen) {
      setLocalList(pendingRecurrences);
      // Initialise les états avec les valeurs suggérées
      const init = {};
      for (const r of pendingRecurrences) {
        init[r.id] = {
          categoryId: r.suggested_category_id ?? null,
          vatRate: 20,
        };
      }
      setItemStates(init);
      setAllDone(false);
    }
  }, [isOpen, pendingRecurrences]);

  // Fermeture clavier Escape
  useEffect(() => {
    if (!isOpen) return;
    function onKey(e) { if (e.key === 'Escape') onClose?.(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  // Auto-close quand liste vide après traitement
  useEffect(() => {
    if (allDone && isOpen) {
      const t = setTimeout(() => onClose?.(), 500);
      return () => clearTimeout(t);
    }
  }, [allDone, isOpen, onClose]);

  function removeItem(id) {
    setLocalList((prev) => {
      const next = prev.filter((r) => r.id !== id);
      if (next.length === 0) setAllDone(true);
      return next;
    });
  }

  function updateItemState(id, patch) {
    setItemStates((prev) => ({
      ...prev,
      [id]: { ...prev[id], ...patch },
    }));
  }

  const handleAction = useCallback(
    (recurrence, status) => {
      const state = itemStates[recurrence.id] ?? {};
      patchRecurrence.mutate(
        {
          id: recurrence.id,
          status,
          ...(status === 'confirmed' || status === 'punctual'
            ? {
                category_id: state.categoryId,
                vat_rate: state.vatRate,
              }
            : {}),
        },
        {
          onSuccess: () => {
            removeItem(recurrence.id);
            qc.invalidateQueries({ queryKey: ['forecasts', 'tree'] });
          },
        }
      );
    },
    [itemStates, patchRecurrence, qc]
  );

  return (
    <>
      {/* Overlay */}
      <div
        className={`fixed inset-0 z-40 transition-opacity duration-200 bg-black/20 ${
          isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        aria-hidden
        onClick={onClose}
      />

      {/* Panel */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Récurrences détectées à valider"
        className={`
          fixed z-50 bg-white shadow-2xl transition-transform duration-200 ease-out
          inset-x-0 bottom-0 rounded-t-2xl max-h-[92dvh] overflow-y-auto
          ${isOpen ? 'translate-y-0' : 'translate-y-full'}
          lg:inset-x-auto lg:right-0 lg:top-0 lg:bottom-0 lg:h-dvh lg:w-[520px] lg:rounded-none lg:max-h-none
          ${isOpen ? 'lg:translate-x-0' : 'lg:translate-x-full'}
        `}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3 sticky top-0 bg-white z-10">
          <div>
            <h2 className="text-base font-semibold text-slate-900">Récurrences détectées</h2>
            <p className="text-xs text-slate-500">
              {localList.length > 0
                ? `${localList.length} à traiter`
                : 'Toutes traitées'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors"
            aria-label="Fermer"
          >
            <X size={18} />
          </button>
        </div>

        {/* Contenu */}
        <div className="px-4 py-3 space-y-4">
          {allDone || localList.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center gap-2">
              <p className="text-sm font-medium text-slate-700">
                Toutes les récurrences détectées ont été traitées
              </p>
              <p className="text-xs text-slate-400">Fermeture automatique…</p>
            </div>
          ) : (
            localList.map((recurrence) => (
              <RecurrenceItem
                key={recurrence.id}
                recurrence={recurrence}
                state={itemStates[recurrence.id] ?? { categoryId: null, vatRate: 20 }}
                onStateChange={(patch) => updateItemState(recurrence.id, patch)}
                onAction={(status) => handleAction(recurrence, status)}
                isPending={patchRecurrence.isPending}
              />
            ))
          )}
        </div>
      </div>
    </>
  );
}

function RecurrenceItem({ recurrence, state, onStateChange, onAction, isPending }) {
  const label = recurrence.label_sample ?? recurrence.label_pattern ?? '—';
  const freqLabel = FREQ_LABELS[recurrence.frequency] ?? recurrence.frequency ?? '';
  const amountCents = recurrence.amount_cents ?? 0;
  const confidence = recurrence.confidence != null
    ? Math.round(recurrence.confidence * 100)
    : null;

  return (
    <div className="border border-slate-200 rounded-xl p-3 space-y-3">
      {/* En-tête item */}
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-slate-900 truncate">{label}</p>
          <p className="text-xs text-slate-500 mt-0.5">
            {freqLabel}
            {freqLabel && ' · '}
            <span className="tabular-nums font-medium text-slate-700">
              {formatCurrency(Math.abs(amountCents))}
            </span>
            {confidence != null && (
              <span className="ml-1 text-slate-400">
                · confiance {confidence} %
              </span>
            )}
          </p>
        </div>
      </div>

      {/* Catégorie */}
      <div>
        <label className="block text-xs font-medium text-slate-600 mb-1">
          Catégorie
        </label>
        <CategoryCombobox
          value={state.categoryId}
          onChange={(id) => onStateChange({ categoryId: id })}
          placeholder="Choisir une catégorie…"
        />
      </div>

      {/* Taux TVA */}
      <div>
        <label className="block text-xs font-medium text-slate-600 mb-1">
          Taux TVA
        </label>
        <div className="flex gap-1.5 flex-wrap">
          {VAT_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => onStateChange({ vatRate: opt.value })}
              className={`px-2.5 py-1 rounded-lg text-xs font-medium border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 ${
                state.vatRate === opt.value
                  ? 'bg-slate-800 text-white border-slate-800'
                  : 'bg-white text-slate-600 border-slate-300 hover:border-slate-400'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-2 pt-1">
        <button
          type="button"
          onClick={() => onAction('confirmed')}
          disabled={isPending}
          className="flex-1 px-3 py-1.5 rounded-lg bg-slate-800 text-white text-xs font-semibold hover:bg-slate-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-500 disabled:opacity-50 flex items-center justify-center gap-1.5"
        >
          {isPending && <Loader2 size={11} className="animate-spin" />}
          Confirmer
        </button>
        <button
          type="button"
          onClick={() => onAction('punctual')}
          disabled={isPending}
          className="flex-1 px-3 py-1.5 rounded-lg border border-slate-300 text-slate-700 text-xs font-semibold hover:bg-slate-50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 disabled:opacity-50"
        >
          Ponctuel
        </button>
        <button
          type="button"
          onClick={() => onAction('dismissed')}
          disabled={isPending}
          className="px-3 py-1.5 rounded-lg border border-red-200 text-red-600 text-xs font-semibold hover:bg-red-50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-400 disabled:opacity-50"
          aria-label={`Rejeter la récurrence : ${label}`}
        >
          Rejeter
        </button>
      </div>
    </div>
  );
}
